import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Cake, ArrowRight, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = (next) => {
  const redirectUrl = window.location.origin + (next || "/app");
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.user_id) navigate(next);
  }, [user, navigate, next]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) setErr(r.error);
    else navigate(next);
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 font-extrabold text-xl mb-8 justify-center" data-testid="login-logo">
          <span className="w-9 h-9 grid place-items-center" style={{ background: "var(--primary)", color: "var(--primary-fg)", borderRadius: "var(--radius-btn)" }}>
            <Cake size={18} />
          </span>
          Born Day
        </Link>

        <div className="bd-surface p-7">
          <h1 className="text-3xl font-extrabold mb-2" style={{ letterSpacing: "-0.04em" }}>Welcome back.</h1>
          <p className="mb-6" style={{ color: "var(--text-dim)" }}>Pick up where you left off.</p>

          <button onClick={() => googleLogin(next)} className="bd-btn bd-btn-ghost w-full mb-4" data-testid="google-login-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.6 12.2c0-.8-.1-1.5-.2-2.2H12v4.3h6c-.3 1.4-1 2.5-2.2 3.3v2.7h3.5c2-1.9 3.3-4.7 3.3-8.1z" fill="#4285F4" />
              <path d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.4-2-6.3-4.6H2v2.8C3.9 20.5 7.7 23 12 23z" fill="#34A853" />
              <path d="M5.7 14.1c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1V7.1H2c-.8 1.5-1.3 3.1-1.3 4.9s.5 3.4 1.3 4.9l3.7-2.8z" fill="#FBBC05" />
              <path d="M12 5.4c1.6 0 3 .6 4.2 1.7l3.1-3.1C17.5 2.2 15 1 12 1 7.7 1 3.9 3.5 2 7.1l3.7 2.8C6.6 7.4 9.1 5.4 12 5.4z" fill="#EA4335" />
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-4 text-xs" style={{ color: "var(--text-dim)" }}>
            <div className="bd-divider flex-1" /> OR <div className="bd-divider flex-1" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="bd-label">Email</label>
              <input
                className="bd-input"
                type="email"
                value={email}
                required
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="bd-label">Password</label>
              <input
                className="bd-input"
                type="password"
                value={password}
                required
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                autoComplete="current-password"
              />
            </div>
            {err && (
              <div className="text-sm px-3 py-2 rounded" style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }} data-testid="login-error">
                {err}
              </div>
            )}
            <button className="bd-btn w-full" disabled={busy} data-testid="login-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <>Sign in <ArrowRight size={16} /></>}
            </button>
          </form>

          <p className="mt-6 text-sm text-center" style={{ color: "var(--text-dim)" }}>
            New here?{" "}
            <Link to="/register" className="font-semibold" style={{ color: "var(--text)" }} data-testid="link-to-register">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
